/**
 * MemberOperationsToolbar — appears beneath the Dossier Analytics section
 * on every dossier. Lets a signed-in member:
 *
 *   📌 Bookmark           one-tap toggle, persists across reloads
 *   🔎 Follow Investigation one-tap toggle, syncs Member Desk live
 *   💬 Message Contributor opens Archive Dispatch composer with callsign
 *                         (disabled gracefully if contributor is anonymous)
 *   📤 Share              native Web Share API + clipboard fallback
 *
 * Privacy: this toolbar NEVER displays an email. Contributor identity is a
 * callsign only, and only if the contributor opted into Discoverable.
 *
 * Cache invalidation: after every state mutation, emit "members" + "topics"
 * invalidation events so Member Desk + bookmark counts + case-files refetch
 * immediately without a page reload.
 *
 * Anonymous viewers see a sign-in CTA, not the toolbar — but Share + the
 * external Twitter/X / copy link options STILL work without auth.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bookmark, Eye, Mail, Share2, Loader2, Check, ExternalLink,
} from "lucide-react";
import { useMemberAuth } from "../../hooks/useMemberAuth";
import {
  memberBookmarkAdd, memberBookmarkRemove,
  memberFollow, memberUnfollow,
  memberDossierState,
} from "../../lib/api";
import { emitArchiveInvalidate } from "../../lib/archive-events";
import { PinToWallButton } from "../../workbench/PinToWallButton";

// ---- Internal button ------------------------------------------------
// Hoisted so React doesn't tear down the subtree on every parent render.
const ToolbarButton = ({
  active, onClick, busy, busyKey, icon: Icon, label, activeLabel,
  testid, color = "#D92D20", ariaPressed = null,
}) => (
  <button
    onClick={onClick}
    disabled={busyKey != null && busy === busyKey}
    aria-pressed={ariaPressed === null ? undefined : ariaPressed}
    data-testid={testid}
    className={"group flex flex-col items-center justify-center gap-1 min-h-[52px] px-1 py-2 sm:px-3 sm:py-3 border-0 sm:border transition-colors " +
      (active
        ? "text-[var(--c)] sm:border-[var(--c)] sm:bg-[#1a0808]"
        : "text-[#A39E93] hover:text-[var(--c)] sm:border-[#2D2A26] sm:hover:border-[var(--c)]") +
      " disabled:opacity-60"}
    style={{ "--c": color }}
  >
    {busyKey != null && busy === busyKey
      ? <Loader2 size={16} className="animate-spin" />
      : <Icon size={16} fill={active ? color : "none"} />}
    <span className="font-mono text-[9px] uppercase tracking-[0.2em]">
      {active ? activeLabel : label}
    </span>
  </button>
);

export const MemberOperationsToolbar = ({ topic }) => {
  const { token, isMember, loading: authLoading } = useMemberAuth();
  const nav = useNavigate();
  const slug = topic?.slug;

  const [state, setState] = useState(null);    // {bookmarked, followed, ...}
  const [busy, setBusy] = useState(null);      // 'bookmark' | 'follow' | null
  const [shared, setShared] = useState(false); // sticky tick after copy

  const load = useCallback(async () => {
    if (!isMember || !slug || !token) { setState(null); return; }
    try {
      const r = await memberDossierState(token, slug);
      setState(r);
    } catch { /* silent — toolbar still renders share */ }
  }, [token, slug, isMember]);

  useEffect(() => {
    // Wait until the async auth bootstrap settles so we don't fire the
    // first dossier-state / mutation calls with a null token (would 401).
    if (authLoading) return;
    load();
  }, [load, authLoading]);

  // ----- Actions -----------------------------------------------------

  const requireAuth = () => {
    if (!isMember) {
      nav(`/auth/login?next=${encodeURIComponent(window.location.pathname)}`);
      return false;
    }
    return true;
  };

  const toggleBookmark = async () => {
    if (!requireAuth() || !slug) return;
    setBusy("bookmark");
    const prev = state;
    const next = !state?.bookmarked;
    // Optimistic UI
    setState((s) => ({
      ...(s || { bookmarks_count: 0, follows_count: 0 }),
      bookmarked: next,
      bookmarks_count: (s?.bookmarks_count || 0) + (next ? 1 : -1),
    }));
    try {
      await (next ? memberBookmarkAdd : memberBookmarkRemove)(token, slug);
      toast.success(next ? "Pinned to Member Desk" : "Bookmark removed");
      emitArchiveInvalidate(["members", "topics"]);
    } catch (e) {
      // Roll back optimistic update to last known good snapshot
      setState(prev);
      toast.error(e?.response?.data?.detail || "Bookmark failed");
    } finally { setBusy(null); }
  };

  const toggleFollow = async () => {
    if (!requireAuth() || !slug) return;
    setBusy("follow");
    const prev = state;
    const next = !state?.followed;
    setState((s) => ({
      ...(s || { bookmarks_count: 0, follows_count: 0 }),
      followed: next,
      follows_count: (s?.follows_count || 0) + (next ? 1 : -1),
    }));
    try {
      await (next ? memberFollow : memberUnfollow)(token, slug);
      toast.success(next ? "Now following this investigation" : "Unfollowed");
      emitArchiveInvalidate(["members", "topics"]);
    } catch (e) {
      setState(prev);
      toast.error(e?.response?.data?.detail || "Follow failed");
    } finally { setBusy(null); }
  };

  const messageContributor = () => {
    if (!requireAuth()) return;
    const cs = state?.contributor_callsign;
    if (!cs) {
      toast("Contributor is anonymous · cannot route a dispatch");
      return;
    }
    nav(`/inbox?to=${encodeURIComponent(cs)}`);
  };

  const sharePage = async () => {
    const url = window.location.href;
    const title = topic?.title ? `The Archive — ${topic.title}` : "The Archive";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        toast.success("Shared");
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        toast.success("Link copied");
        setTimeout(() => setShared(false), 3500);
      }
    } catch { /* user cancelled — silent */ }
  };

  // ----- Render ------------------------------------------------------

  if (!slug) return null;

  return (
    <section className="mt-5 sm:mt-6 mb-2" data-testid="member-ops-toolbar">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-1.5 sm:mb-2">
        // MEMBER OPERATIONS
      </div>
      <div className="grid grid-cols-4 gap-0 sm:gap-2 border-y border-[#2D2A26] divide-x divide-[#2D2A26] sm:border-y-0 sm:divide-x-0">
        <ToolbarButton
          active={!!state?.bookmarked}
          onClick={toggleBookmark}
          busy={busy} busyKey="bookmark"
          icon={Bookmark}
          label="Bookmark"
          activeLabel="Saved"
          ariaPressed={!!state?.bookmarked}
          color="#E0A526"
          testid="member-ops-bookmark"
        />
        <ToolbarButton
          active={!!state?.followed}
          onClick={toggleFollow}
          busy={busy} busyKey="follow"
          icon={Eye}
          label="Follow"
          activeLabel="Following"
          ariaPressed={!!state?.followed}
          color="#5DD4B3"
          testid="member-ops-follow"
        />
        <ToolbarButton
          active={false}
          onClick={messageContributor}
          busy={busy} busyKey={null}
          icon={Mail}
          label={state?.contributor_callsign ? "Message" : "Anonymous"}
          activeLabel="Message"
          color="#9FA8D4"
          testid="member-ops-message"
        />
        <ToolbarButton
          active={shared}
          onClick={sharePage}
          busy={busy} busyKey={null}
          icon={shared ? Check : Share2}
          label="Share"
          activeLabel="Copied"
          color="#D92D20"
          testid="member-ops-share"
        />
      </div>
      {isMember && (
        <div className="mt-2">
          <PinToWallButton type="dossier" refId={slug} />
        </div>
      )}
      {isMember && state && (
        <div className="mt-1.5 sm:mt-2 flex items-center justify-between text-[10px] font-mono text-[#757065]" data-testid="member-ops-counts">
          <span>{state.bookmarks_count} bookmark{state.bookmarks_count === 1 ? "" : "s"} · {state.follows_count} investigation{state.follows_count === 1 ? "" : "s"} followed</span>
          <a href="/members" className="hover:text-[#D92D20] inline-flex items-center gap-1" data-testid="member-ops-desk-link">
            Open Member Desk <ExternalLink size={9} />
          </a>
        </div>
      )}
      {!isMember && (
        <p className="mt-1.5 sm:mt-2 text-[10px] font-mono text-[#757065] leading-snug" data-testid="member-ops-anon-hint">
          Sign in to bookmark, follow, and message the contributor.
        </p>
      )}
    </section>
  );
};

export default MemberOperationsToolbar;
